'use client'

import { useState, useRef, useEffect } from 'react'
// Replaced lucide-react icons with emoji to avoid Runtime ReferenceError

interface Option {
  value: string
  label: string
  description?: string
  icon?: React.ReactNode
}

interface CustomDropdownProps {
  options: Option[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export default function CustomDropdown({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select option...", 
  className = "",
  disabled = false 
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.value === value)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-4 py-3 text-left
          bg-dark-700 border border-dark-600 rounded-lg
          text-white placeholder-gray-400
          hover:bg-dark-600 hover:border-dark-500
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}
        `}
      >
        <div className="flex items-center space-x-3">
          {selectedOption?.icon && (
            <div className="text-gray-400">
              {selectedOption.icon}
            </div>
          )}
          <div>
            <div className="text-white">
              {selectedOption ? selectedOption.label : placeholder}
            </div>
            {selectedOption?.description && (
              <div className="text-xs text-gray-400 mt-0.5">
                {selectedOption.description}
              </div>
            )}
          </div>
        </div>
        <span 
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 inline-block ${
            isOpen ? 'transform rotate-180' : ''
          }`} 
        >⌄</span>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-dark-700 border border-dark-600 rounded-lg dropdown-shadow animate-slide-up">
          <div className="py-1 max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-dark-500 scrollbar-track-dark-700">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full flex items-center justify-between px-4 py-3 text-left
                  hover:bg-dark-600 transition-colors duration-150
                  ${value === option.value ? 'bg-primary-900/30 text-primary-300' : 'text-white'}
                `}
              >
                <div className="flex items-center space-x-3">
                  {option.icon && (
                    <div className={`${value === option.value ? 'text-primary-400' : 'text-gray-400'}`}>
                      {option.icon}
                    </div>
                  )}
                  <div>
                    <div className={`${value === option.value ? 'text-primary-300 font-medium' : 'text-white'}`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {option.description}
                      </div>
                    )}
                  </div>
                </div>
                {value === option.value && (
                  <span className="h-4 w-4 text-primary-400">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}